import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import useActiveTenantRole from 'src/hooks/useActiveTenantRole';
import useAuth from 'src/hooks/useAuth';
import { apiGet } from 'src/lib/api/axios';

/**
 * Fetches the user's remaining message quota from the backend.
 * Returns the number directly, or null if there is no quota.
 * For guest users, quota is always 0 (no fetch).
 */
export const useChatQuota = () => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isStandardMember: isQuotaApplicable } = useActiveTenantRole();

  const userId = user?.user_id;
  const tenantCode = user?.getActiveTenant(pathname)?.code;
  // Quota is per user and per tenant. A shared key keeps the previous
  // account's cached 0 until staleTime, which disables chat for the next person.
  const queryKey = ['chatQuota', userId, tenantCode] as const;

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<number | null> => {
      try {
        const response = await apiGet<number | null>('/chat/chats/remaining-quota', {
          meta: {
            globalError: false,
            context: 'quota-fetch',
            background: true
          }
        });
        return response.data;
      } catch (error: any) {
        // Return null on error to indicate quota data is unavailable
        return null;
      }
    },
    enabled: isQuotaApplicable && userId != null,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  });

  /**
   * Optimistically decrement the quota by 1.
   * Only decrements if quota is not null and > 0.
   */
  const decrementQuota = () => {
    queryClient.setQueryData<number | null>(queryKey, (old) => {
      if (old !== null && old !== undefined && old > 0) {
        return old - 1;
      }
      return old;
    });
  };

  /**
   * Refetch quota from the backend to get the accurate value.
   */
  const refetchQuota = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  return {
    ...query,
    data: isQuotaApplicable ? query.data : 0,
    // Guests are not quota-limited; `data` is forced to 0 for them, but that
    // must not read as "quota exceeded". A missing user id is not a confirmed zero either.
    isQuotaConfirmedZero:
      isQuotaApplicable &&
      userId != null &&
      query.isFetched &&
      query.data === 0 &&
      !query.isFetching &&
      !query.isRefetching,
    decrementQuota,
    refetchQuota
  };
};
